import { ChevronDown, Loader } from '@firmable/icons';
import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import React from 'react';

function Root({ children, ...rest }: Select.SelectProps) {
  return <Select.Root {...rest}>{children}</Select.Root>;
}

type SelectSize = 'lg' | 'sm';

interface TriggerProps extends Select.SelectTriggerProps {
  isLoading?: boolean;
  size?: SelectSize;
}

const triggerSizeStyles: Record<SelectSize, string> = {
  lg: 'p-sm gap-sm rounded-radius-md',
  sm: 'px-sm py-xs gap-xs rounded-radius-sm',
};

function Trigger({ className, children, isLoading, size = 'lg', ...rest }: TriggerProps) {
  const iconSize = size === 'lg' ? 16 : 14;

  return (
    <Select.Trigger
      className={clsx(
        'flex justify-between items-center bg-surface-white border outline-none transition-colors',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        'border-stroke-light hover:border-stroke-dark',
        'focus:border-stroke-interactive focus:ring-1 focus:ring-stroke-interactive',
        'text-content-dark',
        triggerSizeStyles[size],
        className,
      )}
      {...rest}>
      {children}
      <Select.Icon className="text-content-dark">
        {isLoading ? <Loader.Circle size={iconSize} /> : <ChevronDown size={iconSize} />}
      </Select.Icon>
    </Select.Trigger>
  );
}

function Portal({ children, ...rest }: Select.SelectPortalProps) {
  return <Select.Portal {...rest}>{children}</Select.Portal>;
}

function Value({ children, ...rest }: Select.SelectValueProps) {
  return <Select.Value {...rest}>{children}</Select.Value>;
}

function Group({ children, ...rest }: Select.SelectGroupProps) {
  return <Select.Group {...rest}>{children}</Select.Group>;
}

function Label({ children, ...rest }: Select.SelectLabelProps) {
  return <Select.Label {...rest}>{children}</Select.Label>;
}

function Separator({ children, ...rest }: Select.SelectSeparatorProps) {
  return <Select.Separator {...rest}>{children}</Select.Separator>;
}

export interface ContentProps extends Select.SelectContentProps {
  contentClassName?: string;
}

const Content = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ children, className, contentClassName, ...rest }, ref) => {
    return (
      <Select.Content position="popper" sideOffset={3} className={contentClassName} {...rest} ref={ref}>
        <Select.Viewport
          className={clsx(
            'min-w-[var(--radix-popper-anchor-width)] rounded-radius-sm shadow-overlay bg-surface-white p-sm flex flex-col gap-xs',
            className,
          )}>
          {children}
        </Select.Viewport>
      </Select.Content>
    );
  },
);

Content.displayName = 'SelectContent';

export interface ItemProps extends Select.SelectItemProps {
  isSelected: boolean;
}

const Item = React.forwardRef<HTMLDivElement, ItemProps>(({ children, className, isSelected, ...rest }, ref) => {
  return (
    <Select.Item
      ref={ref}
      className={clsx(
        'cursor-pointer outline-none rounded-radius-md p-sm text-body-large',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        {
          'bg-surface-white text-content-dark hover:bg-surface-light': !isSelected,
          'bg-surface-interactive-lighter text-content-interactive hover:bg-surface-interactive-light': isSelected,
        },
        className,
      )}
      {...rest}>
      <Select.ItemText>{children}</Select.ItemText>
    </Select.Item>
  );
});

Item.displayName = 'SelectItem';

function ItemText({ children, className }: Select.SelectItemTextProps) {
  return <Select.ItemText className={clsx(className)}>{children}</Select.ItemText>;
}
function ItemIndicator({ children, className }: Select.SelectItemIndicatorProps) {
  return <Select.ItemIndicator className={clsx(className)}>{children}</Select.ItemIndicator>;
}

export { Content, Group, Item, ItemIndicator, ItemText, Label, Portal, Root, Separator, Trigger, Value };
